import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ContentApiErrorState from '@/components/common/ContentErrorState';
import FormMessage from '@/components/common/FormMessage';
import { queryKeys } from '@/lib/queryKeys';
import { getUserFriendlyMessage } from '@/services/apiError';
import {
  createMyTemplateOverride,
  deleteTemplateOverride,
  listMyTemplateOverrides,
  listPublishedTemplates,
  patchTemplateOverride,
  type MyTemplateOverrideListItem,
} from '@/services/templateSystemApi';

interface FormState {
  templateId: string;
  name: string;
  systemPromptOverride: string;
  planningPromptOverride: string;
  generationPromptOverride: string;
  isActive: boolean;
}

const INITIAL_FORM: FormState = {
  templateId: '',
  name: '',
  systemPromptOverride: '',
  planningPromptOverride: '',
  generationPromptOverride: '',
  isActive: true,
};

export default function CustomTemplatesPage() {
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM);
  const [editingOverrideId, setEditingOverrideId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const publishedTemplatesQuery = useQuery({
    queryKey: queryKeys.templates.published({}),
    queryFn: () => listPublishedTemplates({}),
    retry: 1,
  });

  const myTemplatesQuery = useQuery({
    queryKey: queryKeys.templateOverrides.list({}),
    queryFn: () => listMyTemplateOverrides({}),
    retry: 1,
  });

  useEffect(() => {
    if (formState.templateId) {
      return;
    }
    const firstTemplate = publishedTemplatesQuery.data?.[0];
    if (!firstTemplate) {
      return;
    }
    setFormState((prev) => ({ ...prev, templateId: firstTemplate.id }));
  }, [formState.templateId, publishedTemplatesQuery.data]);

  const sourceTemplateLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const template of publishedTemplatesQuery.data ?? []) {
      map.set(template.id, template.name);
    }
    return map;
  }, [publishedTemplatesQuery.data]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      setError('请填写自定义模板名称');
      return;
    }
    if (!editingOverrideId && !formState.templateId) {
      setError('请先选择模板来源');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: trimmedName,
        systemPromptOverride: formState.systemPromptOverride,
        planningPromptOverride: formState.planningPromptOverride,
        generationPromptOverride: formState.generationPromptOverride,
        isActive: formState.isActive,
      };

      if (editingOverrideId) {
        await patchTemplateOverride(editingOverrideId, payload);
        setSuccess('自定义模板已更新');
      } else {
        await createMyTemplateOverride(formState.templateId, payload);
        setSuccess('自定义模板已创建');
      }

      await myTemplatesQuery.refetch();
      resetForm();
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '保存自定义模板失败'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingOverrideId(null);
    setFormState((prev) => ({
      ...INITIAL_FORM,
      templateId: prev.templateId || publishedTemplatesQuery.data?.[0]?.id || '',
    }));
  };

  const startEdit = (item: MyTemplateOverrideListItem) => {
    setEditingOverrideId(item.id);
    setError('');
    setSuccess('');
    setFormState({
      templateId: item.templateId,
      name: item.name ?? '',
      systemPromptOverride: item.systemPromptOverride ?? '',
      planningPromptOverride: item.planningPromptOverride ?? '',
      generationPromptOverride: item.generationPromptOverride ?? '',
      isActive: item.isActive,
    });
  };

  const activateTemplate = async (item: MyTemplateOverrideListItem) => {
    if (item.isActive) {
      return;
    }
    setWorkingId(item.id);
    setError('');
    setSuccess('');
    try {
      await patchTemplateOverride(item.id, { isActive: true });
      await myTemplatesQuery.refetch();
      setSuccess('已切换为当前激活模板');
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '切换激活模板失败'));
    } finally {
      setWorkingId(null);
    }
  };

  const removeTemplate = async (item: MyTemplateOverrideListItem) => {
    const accepted = window.confirm(`确认删除自定义模板「${item.name ?? '未命名模板'}」吗？`);
    if (!accepted) {
      return;
    }
    setWorkingId(item.id);
    setError('');
    setSuccess('');
    try {
      await deleteTemplateOverride(item.id);
      await myTemplatesQuery.refetch();
      if (editingOverrideId === item.id) {
        resetForm();
      }
      setSuccess('自定义模板已删除');
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '删除自定义模板失败'));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className='max-w-[1080px] mx-auto px-6 py-12'>
      <div className='mb-8'>
        <h1
          className='font-semibold text-apple-near-black mb-2'
          style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
        >
          我的自定义模板
        </h1>
        <p className='text-apple-gray text-[0.9375rem]'>普通用户可基于站点模板创建个人模板，不影响系统模板。</p>
      </div>

      {(error || success) ? (
        <div className='space-y-2 mb-5'>
          {error ? <FormMessage tone='error' message={error} /> : null}
          {success ? <FormMessage tone='success' message={success} /> : null}
        </div>
      ) : null}

      <div className='grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6'>
        <form className='bg-white rounded-2xl p-6 shadow-card-sm space-y-4 h-fit' onSubmit={handleSubmit}>
          <h2 className='font-semibold text-apple-near-black text-[1.125rem]'>
            {editingOverrideId ? '编辑自定义模板' : '创建自定义模板'}
          </h2>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='custom-template-source'>
              模板来源
            </label>
            <select
              id='custom-template-source'
              className='input'
              value={formState.templateId}
              onChange={(event) => setFormState((prev) => ({ ...prev, templateId: event.target.value }))}
              disabled={Boolean(editingOverrideId) || saving}
            >
              {(publishedTemplatesQuery.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='custom-template-name'>
              自定义模板名称
            </label>
            <input
              id='custom-template-name'
              className='input'
              value={formState.name}
              onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
              disabled={saving}
              required
            />
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='custom-template-system'>
              System Prompt
            </label>
            <textarea
              id='custom-template-system'
              className='input min-h-24'
              value={formState.systemPromptOverride}
              onChange={(event) => setFormState((prev) => ({ ...prev, systemPromptOverride: event.target.value }))}
              disabled={saving}
            />
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='custom-template-planning'>
              Planning Prompt
            </label>
            <textarea
              id='custom-template-planning'
              className='input min-h-24'
              value={formState.planningPromptOverride}
              onChange={(event) => setFormState((prev) => ({ ...prev, planningPromptOverride: event.target.value }))}
              disabled={saving}
            />
          </div>

          <div className='space-y-1.5'>
            <label className='block text-[0.875rem] font-medium text-apple-near-black' htmlFor='custom-template-generation'>
              Generation Prompt
            </label>
            <textarea
              id='custom-template-generation'
              className='input min-h-24'
              value={formState.generationPromptOverride}
              onChange={(event) => setFormState((prev) => ({ ...prev, generationPromptOverride: event.target.value }))}
              disabled={saving}
            />
          </div>

          <label className='flex items-center gap-2 text-[0.875rem] text-apple-near-black'>
            <input
              type='checkbox'
              checked={formState.isActive}
              onChange={(event) => setFormState((prev) => ({ ...prev, isActive: event.target.checked }))}
              disabled={saving}
            />
            保存后立即激活
          </label>

          <div className='flex gap-3'>
            <button type='submit' className='btn btn-primary' disabled={saving}>
              {saving ? '保存中…' : editingOverrideId ? '保存修改' : '创建自定义模板'}
            </button>
            {editingOverrideId ? (
              <button type='button' className='btn btn-secondary' onClick={resetForm} disabled={saving}>
                取消编辑
              </button>
            ) : null}
          </div>
        </form>

        <section className='bg-white rounded-2xl p-6 shadow-card-sm'>
          <h2 className='font-semibold text-apple-near-black text-[1.125rem] mb-4'>我已创建的模板</h2>

          {publishedTemplatesQuery.isLoading || myTemplatesQuery.isLoading ? (
            <p className='text-apple-gray text-[0.9375rem]'>加载中…</p>
          ) : null}

          {publishedTemplatesQuery.isError ? (
            <div className='mb-4'>
              <ContentApiErrorState error={publishedTemplatesQuery.error} onRetry={() => void publishedTemplatesQuery.refetch()} />
            </div>
          ) : null}

          {myTemplatesQuery.isError ? (
            <div className='mb-4'>
              <ContentApiErrorState error={myTemplatesQuery.error} onRetry={() => void myTemplatesQuery.refetch()} />
            </div>
          ) : null}

          {!myTemplatesQuery.isLoading && !myTemplatesQuery.isError && (myTemplatesQuery.data?.length ?? 0) === 0 ? (
            <p className='text-apple-gray text-[0.9375rem]'>还没有自定义模板，先创建一个吧。</p>
          ) : null}

          <div className='space-y-3'>
            {(myTemplatesQuery.data ?? []).map((item) => (
              <article key={item.id} className='border border-apple-border rounded-xl p-4'>
                <div className='flex items-start justify-between gap-3'>
                  <div>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-medium text-apple-near-black text-[0.95rem]'>{item.name || '未命名模板'}</h3>
                      {item.isActive ? <span className='text-[0.75rem] px-2 py-0.5 rounded-full bg-green-100 text-green-700'>激活中</span> : null}
                    </div>
                    <p className='text-apple-gray text-[0.8125rem] mt-1'>
                      来源：{item.template?.name ?? sourceTemplateLabel.get(item.templateId) ?? item.templateId}
                    </p>
                  </div>
                  <div className='flex gap-2'>
                    <button type='button' className='btn btn-secondary' onClick={() => startEdit(item)}>
                      编辑
                    </button>
                    <button
                      type='button'
                      className='btn btn-secondary'
                      disabled={item.isActive || workingId === item.id}
                      onClick={() => void activateTemplate(item)}
                    >
                      设为激活
                    </button>
                    <button
                      type='button'
                      className='btn btn-danger'
                      disabled={workingId === item.id}
                      onClick={() => void removeTemplate(item)}
                    >
                      删除
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
